import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { BenchmarkTriple } from "@/components/data/BenchmarkTriple";
import { ResidualWithBudget } from "@/components/data/ResidualWithBudget";

const LABELS = {
  chlorination_bz: "Chlorination — Benzene Chain", nitration_chlorobenzene: "Nitration — Chlorobenzene route",
  hydrogenation_ncb: "Hydrogenation — NCB route", nitration_toluene: "Nitration — Toluene route",
  hydrogenation_nitrotoluene: "Hydrogenation — Nitrotoluene route", granulation: "Granulation / Valorization",
  chiller01: "Chiller CH-01 (shared equipment)", pump101: "Feed Pump P-101 (Benzene feed, shared equipment)",
  hx201: "Heat Exchanger HX-201 (Hydrogenation heat recovery)", pump102: "Feed Pump P-102 (Toluene feed, shared equipment)",
  pump301: "Dosing Pump P-301 (Ca(OH)\u2082 slurry, shared equipment)", distillation_bz: "Distillation Column COL-301 (Benzene finishing)",
  distillation_tl: "Distillation Column COL-302 (Toluene finishing)",
  tank301: "Finished Product Storage Tank TK-301 (Benzene)",
};

const KEY_LABELS = {
  route: "Route", feed_rate: "Feed rate (kg/hr)", cl2_rate: "Cl2 rate (kg/hr)", benzene_rate: "Benzene rate (kg/hr)",
  hno3_rate: "HNO3 rate (kg/hr)", temp: "Temperature (\u00b0C)", cooling_duty: "Cooling duty (kW)",
  h2_rate: "H2 rate (kg/hr)", catalyst_eff: "Catalyst efficiency (%)", conversion: "Conversion (%)",
  product_kg: "Product (kg/hr)", h2_consumption_efficiency: "H2 use efficiency", h2_actual_ratio: "H2:feed ratio",
  mcb_split: "MCB split (%)", dcb_split: "DCB split (%)", loss_split: "Loss split (%)", stoich_ratio: "Stoichiometric ratio",
  mcb_kg: "MCB (kg/hr)", dcb_kg: "DCB (kg/hr)", loss_kg: "Loss (kg/hr)", hcl_kg: "HCl byproduct (kg/hr)",
  heat_kw: "Heat of reaction (kW)", deficit_fraction: "Cooling deficit fraction", mtsr: "MTSR (\u00b0C)",
  mtt: "MTT (\u00b0C)", t_d24: "T_D24 (\u00b0C)", cooling_margin: "Cooling margin (K)", criticality_class: "Criticality class",
  distance_to_next_class: "Distance to next class (K)", isomers: "Isomer split", hcl_rate: "HCl rate (kg/hr)",
  ca_oh2_rate: "Ca(OH)2 rate (kg/hr)", actual_ratio: "Actual ratio", recovery_eff: "Recovery efficiency (%)",
  cacl2_kg: "CaCl2 output (kg/hr)", purity_pct: "Purity (%)", duty: "Duty (kW)", power: "Power draw (kW)",
  fouling_factor: "Fouling factor", effective_duty: "Effective duty (kW)", cop_actual: "COP",
  flow_rate: "Flow rate (kg/hr)", discharge_pressure: "Discharge pressure (bar)", motor_power: "Motor power (kW)",
  hydraulic_power: "Hydraulic power (kW)", efficiency: "Pump efficiency", npsh_available: "NPSH available (m)",
  npsh_required: "NPSH required (m)", npsh_margin: "NPSH margin (m)", hot_in_temp: "Hot-side inlet temp (\u00b0C)",
  hot_out_temp: "Hot-side outlet temp (\u00b0C)", cold_in_temp: "Cold-side inlet temp (\u00b0C)", cold_out_temp: "Cold-side outlet temp (\u00b0C)",
  effectiveness: "Heat exchanger effectiveness", approach_temp: "Approach temperature (\u00b0C)",
  reflux_ratio: "Reflux ratio", reboiler_duty: "Reboiler duty (kW)", overhead_temp: "Overhead temperature (\u00b0C)",
  specific_duty: "Specific reboiler duty (kWh/t)",
  level_pct: "Level (%)", n2_blanket_pressure: "N\u2082 blanket pressure (barg)", inventory_kg: "Inventory (kg)",
  days_cover: "Days of cover", breathing_loss_kg_hr: "Breathing loss (kg/hr)",
};

function humanizeKey(k) {
  return KEY_LABELS[k] || k.replace(/_/g, " ");
}

export default function TwinDetail() {
  const { twinId } = useParams();
  const { data, error } = usePolling(() => api.get(`/twins/${twinId}`).then((r) => r.data), 5000, [twinId]);

  if (error) return <div className="text-sm text-[var(--oim-text-muted)]" data-testid="twin-detail-error">Could not load twin.</div>;
  if (!data) return <div className="text-sm text-[var(--oim-text-muted)]" data-testid="twin-detail-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="twin-detail-page">
      <h1 className="text-2xl font-bold font-heading text-[var(--oim-text)]">{LABELS[twinId] || twinId}</h1>

      <div className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }} data-testid={`twin-card-${twinId}`}>
        <div className="text-sm font-heading font-bold text-[var(--oim-text)] mb-3">Twin Outputs</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 font-mono-data text-xs">
          {Object.entries(data.outputs).map(([k, v]) => (
            <div key={k}>
              <span className="text-[9px] text-[var(--oim-text-dim)] block uppercase">{humanizeKey(k)}</span>
              <span className="text-[var(--oim-text)] font-semibold">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }} data-testid="twin-basis" data-basis={data.basis}>
        <div data-testid="twin-confidence" data-confidence={data.confidence}>
          <BenchmarkTriple
            label="Primary performance quantity" unit={data.unit}
            actual={data.actual} shouldbe={data.shouldbe} golden={data.golden}
            basis={data.basis} confidence={data.confidence} limitedBy={data.limited_by}
            testId={`benchmark-${twinId}`}
          />
        </div>
      </div>

      <div className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }} data-testid="twin-residual">
        <ResidualWithBudget residual={data.residual} errorBudget={data.error_budget} unit={data.unit} testId={`twin-residual-${twinId}`} />
      </div>

      <div className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }} data-testid="twin-candidate-causes">
        <div className="text-sm font-heading font-bold text-[var(--oim-text)] mb-3">Ranked candidate causes</div>
        <div className="space-y-2">
          {data.candidate_causes.map((c, i) => (
            <div key={i} className="flex items-start justify-between text-xs" data-testid={`twin-cause-${twinId}-${i}`}>
              <span className="text-[var(--oim-text-secondary)]">{i + 1}. {c.label}</span>
              <span className="text-[var(--oim-text-dim)]">{c.evidence}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
